// Admin management for the pre-snapshot data gate: publish "we need one more
// piece of data" requirements (an existing profile field, or a one-off custom
// question), watch completion counts, and retire requirements. Publishing
// pushes a notification to every member who hasn't already satisfied it.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Check,
  Users,
  Clock,
  Ban,
  Send,
  ChevronDown,
  Loader2,
  Building2,
} from 'lucide-react';
import { profileRequirementsAPI, forumsAPI } from '../api/client';
import type { ProfileRequirementAdmin, ProfileRequirementAnswerRow, ForumListItem } from '../types';

export default function RequirementsAdmin() {
  const [requirements, setRequirements] = useState<ProfileRequirementAdmin[]>([]);
  const [fieldOptions, setFieldOptions] = useState<{ value: string; label: string }[]>([]);
  const [forums, setForums] = useState<ForumListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKind, setNewKind] = useState<'profile_field' | 'custom_question'>('profile_field');
  const [newField, setNewField] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [newForumId, setNewForumId] = useState<number | ''>('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [answersById, setAnswersById] = useState<Record<number, ProfileRequirementAnswerRow[]>>({});
  const [answersLoadingId, setAnswersLoadingId] = useState<number | null>(null);

  // Expand/collapse a requirement's per-member responses, lazy-loading them
  // the first time it is opened.
  const toggleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!answersById[id]) {
      setAnswersLoadingId(id);
      try {
        const rows = await profileRequirementsAPI.getAnswers(id);
        setAnswersById(prev => ({ ...prev, [id]: rows }));
      } catch {
        setMessage({ type: 'error', text: 'Failed to load member responses' });
        setExpandedId(null);
      } finally {
        setAnswersLoadingId(null);
      }
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [reqs, fields, forumList] = await Promise.all([
        profileRequirementsAPI.listAdmin(),
        profileRequirementsAPI.getFields(),
        forumsAPI.getAll(),
      ]);
      setRequirements(reqs);
      setFieldOptions(fields);
      setForums(forumList);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load data requests' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const canPublish =
    newKind === 'profile_field' ? newField !== '' : newQuestion.trim() !== '';

  const handleCreate = async () => {
    if (!canPublish || isSaving) return;
    setIsSaving(true);
    try {
      await profileRequirementsAPI.create({
        ...(newKind === 'profile_field'
          ? { kind: 'profile_field' as const, field_name: newField }
          : { kind: 'custom_question' as const, question_text: newQuestion.trim() }),
        forum_id: newForumId === '' ? null : newForumId,
      });
      setMessage({
        type: 'success',
        text: 'Published — affected members have been notified.',
      });
      setShowCreate(false);
      setNewField('');
      setNewQuestion('');
      setNewForumId('');
      await fetchData();
    } catch {
      setMessage({ type: 'error', text: 'Failed to publish the request' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await profileRequirementsAPI.deactivate(id);
      setMessage({ type: 'success', text: 'Request deactivated' });
      await fetchData();
    } catch {
      setMessage({ type: 'error', text: 'Failed to deactivate' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  const active = requirements.filter((r) => r.active);
  const inactive = requirements.filter((r) => !r.active);

  const renderAnswers = (req: ProfileRequirementAdmin) => {
    if (answersLoadingId === req.id) {
      return (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" /> Loading responses...
        </div>
      );
    }
    const rows = answersById[req.id];
    if (!rows) return null;
    const answered = rows.filter((r) => r.answered);
    const outstanding = rows.filter((r) => !r.answered);
    const valueLabel = req.kind === 'profile_field' ? 'Current value' : 'Answer';
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
        {answered.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {valueLabel} — {answered.length} {answered.length === 1 ? 'member' : 'members'}
            </h4>
            <div className="divide-y divide-gray-50">
              {answered.map((row) => (
                <div key={row.user_id} className="py-2 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                  <Link
                    to={`/members/${row.user_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm font-medium text-navy hover:underline sm:w-48 shrink-0"
                  >
                    {row.name}
                  </Link>
                  <span className="text-sm text-gray-700 flex-1 whitespace-pre-wrap">
                    {String(row.value)}
                  </span>
                  {row.answered_at && (
                    <span className="text-xs text-gray-400 shrink-0">
                      {new Date(row.answered_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {req.kind === 'profile_field' ? 'Still blank' : "Hasn't answered"} — {outstanding.length}
          </h4>
          {outstanding.length === 0 ? (
            <p className="text-sm text-green-700 flex items-center gap-1.5">
              <Check size={14} /> Everyone has completed this.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {outstanding.map((row) => (
                <Link
                  key={row.user_id}
                  to={`/members/${row.user_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-100 hover:bg-amber-100"
                >
                  {row.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRow = (req: ProfileRequirementAdmin) => (
    <div
      key={req.id}
      className={`bg-white rounded-xl shadow-sm border p-5 ${req.active ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}
    >
      <div
        onClick={() => toggleExpand(req.id)}
        className="flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              req.kind === 'profile_field'
                ? 'bg-blue-50 text-blue-700'
                : 'bg-purple-50 text-purple-700'
            }`}>
              {req.kind === 'profile_field' ? 'Profile field' : 'Custom question'}
            </span>
            {!req.active && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                Inactive
              </span>
            )}
            {req.forum_id && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 flex items-center gap-1">
                <Building2 size={10} /> {req.forum_name}
              </span>
            )}
          </div>
          <p className="font-semibold text-gray-900">
            {req.kind === 'profile_field' ? req.field_label : req.question_text}
          </p>
          <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Clock size={12} /> {new Date(req.created_at).toLocaleDateString()}
            </span>
            {req.created_by_name && <span>by {req.created_by_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-navy font-semibold">
              <Users size={16} />
              {req.completed_count} of {req.total_count}
            </div>
            <p className="text-xs text-gray-400">members done</p>
          </div>
          {req.active && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDeactivate(req.id); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
            >
              <Ban size={14} /> Deactivate
            </button>
          )}
          <button
            aria-label={expandedId === req.id ? 'Hide responses' : 'Show responses'}
            aria-expanded={expandedId === req.id}
            className="p-1.5 rounded-lg text-gray-400 hover:text-navy hover:bg-off-white transition"
          >
            <ChevronDown
              size={18}
              className={`transition-transform ${expandedId === req.id ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>
      {expandedId === req.id && renderAnswers(req)}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClipboardList className="text-navy" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Member Data Requests</h1>
            <p className="text-sm text-gray-500">
              Members must complete these before starting a new snapshot.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark text-sm shrink-0"
        >
          <Plus size={16} /> New Request
        </button>
      </div>

      {showCreate && (
        <div className="bg-white rounded-xl shadow-sm border border-gold/30 p-5 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Publish a data request</h3>
          <div className="flex gap-2 mb-4">
            {([
              { value: 'profile_field', label: 'Existing profile field' },
              { value: 'custom_question', label: 'Custom question' },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                onClick={() => setNewKind(opt.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  newKind === opt.value
                    ? 'bg-navy text-white border-navy'
                    : 'border-gray-300 text-gray-600 hover:border-navy'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {newKind === 'profile_field' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Which profile field should members fill in?
              </label>
              <select
                value={newField}
                onChange={(e) => setNewField(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
              >
                <option value="">Select a field...</option>
                {fieldOptions.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                What would you like to ask every member?
              </label>
              <textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
                placeholder="e.g. What is the biggest challenge in your business right now?"
              />
            </div>
          )}

          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Who does this apply to?
            </label>
            <select
              value={newForumId}
              onChange={(e) => setNewForumId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold bg-white"
            >
              <option value="">Everyone (org-wide)</option>
              {forums.map((f) => (
                <option key={f.id} value={f.id}>
                  Only {f.name} ({f.city}, {f.state})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-gray-500 max-w-sm">
              Publishing notifies every affected member who hasn't already
              provided this, and blocks new snapshots until they do.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!canPublish || isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-gold-dark text-white rounded-lg hover:bg-gold-dark text-sm disabled:opacity-50"
              >
                <Send size={16} /> {isSaving ? 'Publishing...' : 'Publish & Notify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {requirements.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
          <ClipboardList className="mx-auto text-gray-300" size={48} />
          <p className="text-gray-500 mt-3">No data requests yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Publish one to collect missing info from every member.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Check size={14} /> Active
              </h2>
              <div className="space-y-3">{active.map(renderRow)}</div>
            </div>
          )}
          {inactive.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Ban size={14} /> Inactive
              </h2>
              <div className="space-y-3">{inactive.map(renderRow)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
